import graphql from "babel-plugin-relay/macro";
import { readInlineData } from "react-relay";

import {
  getDiscountDisplayAmountCents,
  getMatchingAppliedDiscountCents,
} from "../../../utils/getDiscountDisplayAmountCents";
import type {
  getAggregatedOrderFinancialSummary_order$data,
  getAggregatedOrderFinancialSummary_order$key,
} from "./__generated__/getAggregatedOrderFinancialSummary_order.graphql";

export type OrderFinancialSummary = Omit<
  getAggregatedOrderFinancialSummary_order$data,
  " $fragmentType"
>;

export type AggregatedOrderFinancialSummary = {
  subtotalBeforeDiscount: number;
  totalAfterDiscount: number;
  totalDiscounts: number;
  totalTaxes: number;
  serviceChargeTotal: number;
};

type OrderTotals = Omit<
  AggregatedOrderFinancialSummary,
  "subtotalBeforeDiscount"
>;

export const readOrderFinancialSummary = (
  orderRef: getAggregatedOrderFinancialSummary_order$key | null | undefined
): OrderFinancialSummary | null => {
  if (!orderRef) {
    return null;
  }

  return readInlineData(
    getAggregatedOrderFinancialSummaryOrderFragmentString,
    orderRef
  );
};

export const getOrderDiscountTotalCents = (
  order:
    | Pick<
        OrderFinancialSummary,
        "totalDiscount" | "orderDiscounts" | "lineItemGroups"
      >
    | null
    | undefined
): number => {
  if ((order?.totalDiscount ?? 0) > 0) {
    return order?.totalDiscount ?? 0;
  }

  const lineItems =
    order?.lineItemGroups?.flatMap((group) => group?.lineItems ?? []) ?? [];

  return (
    order?.orderDiscounts?.edges?.reduce((sum, edge) => {
      const discount = edge?.node;
      if (!discount) {
        return sum;
      }

      return (
        sum +
        getDiscountDisplayAmountCents(
          discount,
          getMatchingAppliedDiscountCents(discount.squareExternalId, lineItems)
        )
      );
    }, 0) ?? 0
  );
};

export const getAggregatedOrderFinancialSummary = (
  orders: ReadonlyArray<OrderFinancialSummary | null | undefined>
): AggregatedOrderFinancialSummary => {
  const totals = orders.reduce<OrderTotals>(
    (
      { totalAfterDiscount, totalDiscounts, totalTaxes, serviceChargeTotal },
      bookingOrder
    ) => {
      const orderTotalValue = bookingOrder?.total ?? 0;
      const amountToAddToServiceChargeTotal =
        bookingOrder?.serviceCharges?.edges?.reduce(
          (acc, serviceChargeEdge) =>
            acc + (serviceChargeEdge?.node?.amount ?? 0),
          0
        ) ?? 0;
      return {
        totalAfterDiscount: totalAfterDiscount + orderTotalValue,
        totalDiscounts:
          totalDiscounts + getOrderDiscountTotalCents(bookingOrder),
        totalTaxes: totalTaxes + (bookingOrder?.totalTaxes ?? 0),
        serviceChargeTotal:
          serviceChargeTotal + amountToAddToServiceChargeTotal,
      };
    },
    {
      totalAfterDiscount: 0,
      totalDiscounts: 0,
      totalTaxes: 0,
      serviceChargeTotal: 0,
    }
  );

  return {
    ...totals,
    subtotalBeforeDiscount:
      totals.totalAfterDiscount -
      totals.totalTaxes -
      totals.serviceChargeTotal +
      totals.totalDiscounts,
  };
};

const getAggregatedOrderFinancialSummaryOrderFragmentString = graphql`
  fragment getAggregatedOrderFinancialSummary_order on Order @inline {
    total
    totalDiscount
    totalTaxes
    orderDiscounts {
      edges {
        node {
          appliedAmount
          amount
          squareExternalId
        }
      }
    }
    lineItemGroups {
      lineItems {
        ... on BookingLineItem {
          appliedDiscounts {
            amountCents
            squareExternalDiscountId
          }
        }
        ... on RoundLineItem {
          appliedDiscounts {
            amountCents
            squareExternalDiscountId
          }
        }
      }
    }
    serviceCharges {
      edges {
        node {
          amount
        }
      }
    }
  }
`;
